import type { Access, Book, ReaderTrack, ReadingStats } from '@wellread/api';
import { count, plural, readingTime } from '@wellread/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Fragment, lazy, Suspense, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  accessOf,
  day,
  hasPremium,
  markPremiumShown,
  premiumFirst,
  runningOut,
  shownPremium,
} from '../lib/billing';
import { continueTarget, currentBook, isReady, isTech, isUnanalysed, shelfLine } from '../lib/books';
import { bareTitle, chapterQuery, placeName } from '../lib/chapters';
import type { Place } from '../lib/place';
import { useBooks, useContents, useMe, useReadingStats, useTracks } from '../lib/queries';
import { settingsOf } from '../lib/settings';
import { trackFacts, untouched } from '../lib/tracks';
import { useImport } from '../state/upload';
import { Button, Cover, cx, Icon, Meter } from '../ui';
import { AskBarWait, LIBRARY_BAR } from './askbar';

// Only a new reader sees these, so they load on demand.
const Welcome = lazy(() => import('./ShelfStart').then((m) => ({ default: m.Welcome })));
const Building = lazy(() => import('./ShelfStart').then((m) => ({ default: m.Building })));
// Explore's field comes with Explore's code, once the page is up; its shell holds the place till then.
const NewConversation = lazy(() =>
  import('./explore/NewConversation').then((m) => ({ default: m.NewConversation })),
);

function greeting(d = new Date()): string {
  const h = d.getHours();
  return h < 5 ? 'Good evening' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

/**
 * Home: a greeting with the way to add a book, the ask bar, the book you're
 * in the middle of, then what your reading adds up to beside the other books
 * you've started. A book that is waiting for its track is named under the
 * greeting. A new reader sees the welcome instead, with the bar too; one
 * whose first book is still being analysed sees how far that has got. Every
 * book is in the library.
 */
export function Home() {
  const me = useMe();
  const books = useBooks();
  const navigate = useNavigate();
  const importBook = useImport();
  const list = useMemo(() => books.data?.books ?? [], [books.data]);
  const hero = currentBook(list);
  const tracks = useTracks(!!books.data).data?.tracks ?? [];
  // Once the reader has a track of their own, Home says how each is going.
  const byTrack = tracks.length > 0 && !untouched(tracks);
  const building = hero ? undefined : newest(list.filter((b) => !isReady(b) && !isUnanalysed(b)));
  // Read into chapters and waiting to be told which track they are for, newest first.
  const waiting = list
    .filter((b) => b.status === 'awaiting_confirm')
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.metaKey || e.ctrlKey || e.altKey || e.target !== document.body) return;
      if (hero) void navigate(continueTarget(hero));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, hero]);

  const settings = settingsOf(me.data);
  const name = settings.profile.display_name || me.data?.email.split('@')[0] || '';

  // A reader in their free days is shown Premium once, before their first
  // book: its page, in Home's place.
  if (me.data) {
    const first = premiumFirst(accessOf(me.data), shownPremium(me.data.id), {
      books: books.data ? list : undefined,
      loading: books.isPending && books.fetchStatus !== 'paused',
    });
    if (first === 'wait') return null;
    if (first === 'go') return <ToPremium reader={me.data.id} />;
  }

  if (books.data && list.length === 0) {
    return (
      <div className="shpage">
        <Paid />
        <RunningOut />
        <Suspense fallback={null}>
          <Welcome onImport={importBook} tracks={tracks} />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="shpage home">
      <header className="sh-head lib-head">
        {building ? (
          <div>
            <h1>{buildingTitle(building, list.length)}</h1>
            <p className="sh-facts">
              <span>{buildingLine(building)}</span>
            </p>
          </div>
        ) : (
          <div>
            <h1>{name ? `${greeting()}, ${name}` : greeting()}</h1>
          </div>
        )}
        {/* The book the page is already about is named in its heading. */}
        <Waiting books={waiting.filter((b) => b.id !== building?.id)} />
        {hero || building ? (
          <Button className="btn-lg" onClick={importBook}>
            <Icon name="upload" size={16} />
            Add a book
          </Button>
        ) : null}
      </header>

      {books.error ? <p className="error">{books.error.message}</p> : null}

      <Paid />
      <RunningOut />

      {hero || building ? (
        <div className="sh-askbar">
          <Suspense fallback={<AskBarWait {...LIBRARY_BAR} />}>
            <NewConversation placement="bar" skipContinue {...LIBRARY_BAR} />
          </Suspense>
        </div>
      ) : null}

      {hero ? (
        <>
          <Continue book={hero} />
          <div className="sh-split">
            <YourReading concepts={list.some((b) => isReady(b) && isTech(b))} />
            <AlsoReading books={alsoReading(list, hero)} />
          </div>
          {byTrack ? <YourTracks tracks={tracks} /> : null}
          {tracks.length > 0 && !byTrack ? <TracksNote /> : null}
        </>
      ) : building ? (
        <Suspense fallback={null}>
          <Building book={building} />
        </Suspense>
      ) : null}
    </div>
  );
}

function buildingTitle(b: Book, books: number): string {
  if (b.status === 'awaiting_confirm') return `Waiting for you: ${b.title}`;
  return books > 1 ? 'Your books are on their way' : 'Your first book is on its way';
}

function buildingLine(b: Book): string {
  if (b.status === 'uploading' || b.status === 'parsing')
    return 'Reading the file. This takes a few seconds.';
  if (b.status === 'awaiting_confirm')
    return 'Choose its track and wellread analyses it, or read it without analysing.';
  return b.track === 'technical'
    ? 'You can start reading now. Questions arrive when the analysis finishes.'
    : 'You can start reading now. The cast and the recap arrive when the analysis finishes.';
}

const newest = (bs: Book[]) => [...bs].sort((a, b) => (b.created_at > a.created_at ? 1 : -1))[0];

/**
 * A book that has been read into chapters and waits to be told which track
 * it is for: named under the greeting, with the way to its page, where the
 * reader chooses. With several, the newest is named and the rest counted.
 * Presentational, so the gallery can render it.
 */
export function Waiting({ books }: { books: Book[] }) {
  const b = books[0];
  if (!b) return null;
  return (
    <p className="sh-waiting">
      <span>
        Waiting for you: <b>{b.title}</b>
        {books.length > 1 ? ` and ${count(books.length - 1)} more` : ''}
      </span>
      <Link to="/books/$bookId" params={{ bookId: b.id }} className="btn btn-secondary btn-sm">
        Choose its track
      </Link>
    </p>
  );
}

/** The other books you've started, most recently read first: up to three. */
function alsoReading(list: Book[], hero: Book): Book[] {
  return list
    .filter((b) => b.id !== hero.id && b.position)
    .sort((a, b) => (b.position!.updated_at > a.position!.updated_at ? 1 : -1))
    .slice(0, 3);
}

/** The heading above your place and the sentence you were on, once the chapter is at hand. */
function usePlace(b: Book): Place {
  const idx = b.position?.chapter_idx ?? 0;
  const offset = b.position?.char_offset ?? 0;
  const chapter = useQuery({ ...chapterQuery(b.id, idx), enabled: !!b.position });
  const html = chapter.data?.html;
  const [place, setPlace] = useState<{ html?: string; offset?: number; place: Place }>({ place: {} });
  useEffect(() => {
    if (!html) return;
    let live = true;
    void import('../lib/place').then(
      (m) => live && setPlace({ html, offset, place: m.placeIn(html, offset) }),
    );
    return () => {
      live = false;
    };
  }, [html, offset]);
  return place.html === html && place.offset === offset ? place.place : {};
}

function Continue({ book: b }: { book: Book }) {
  const tech = b.track === 'technical';
  // A book kept only to read has nothing held, to review, or of a story to show.
  const analysed = isReady(b);
  const contents = useContents(b.id);
  const place = usePlace(b);
  const idx = b.position?.chapter_idx ?? 0;
  const here = contents.data?.chapters[idx];
  // "Chapter 6 of 14, Replication", or a preface by its own name.
  const where = !b.position
    ? plural(b.numbered || b.chapters, 'chapter')
    : here
      ? here.number
        ? `Chapter ${here.number} of ${b.numbered}, ${bareTitle(here)}`
        : here.title
      : placeName(b);
  const read = b.read_percent;
  return (
    <section className="sh-band" aria-labelledby="sh-continue">
      <Link to="/books/$bookId" params={{ bookId: b.id }} className="sh-band-cover" tabIndex={-1}>
        <Cover title={b.title} author={b.author} src={b.cover_url} ground={b.ground} size="fill" />
      </Link>
      <div className="sh-band-copy">
        <span className="sh-kicker" id="sh-continue">
          Continue reading
        </span>
        <Link to="/books/$bookId" params={{ bookId: b.id }} className="sh-band-title">
          {b.title}
        </Link>
        <span className="sh-by">
          {tech ? b.author : [b.author, b.position ? where : ''].filter(Boolean).join(' · ')}
        </span>
        {tech ? (
          <p className="sh-where">
            {b.position ? `${where}.` : 'Not started yet.'}
            {place.heading ? (
              <>
                {' '}
                You stopped at <em>{place.heading}</em>.
              </>
            ) : null}
          </p>
        ) : place.sentence ? (
          <blockquote className="sh-quote">{`“${place.sentence}”`}</blockquote>
        ) : null}
        <div className="sh-progress">
          <Meter value={read} label="Read" />
          <span>{tech && analysed ? `${read}% read · ${b.held_percent}% held` : `${read}% read`}</span>
        </div>
        <div className="row-flex sh-acts">
          <Link {...continueTarget(b)} className="btn btn-primary btn-lg">
            {b.position ? 'Continue reading' : 'Start reading'} <kbd>↵</kbd>
          </Link>
          {analysed && tech && b.review && b.concepts > 0 ? (
            <Link to="/review/session" search={{ book: b.id }} className="btn btn-secondary btn-lg">
              Review
            </Link>
          ) : null}
          {analysed && !tech ? (
            <>
              <Link to="/books/$bookId/story" params={{ bookId: b.id }} className="btn btn-secondary btn-lg">
                Story so far
              </Link>
              <Link to="/books/$bookId/cast" params={{ bookId: b.id }} className="btn btn-quiet btn-lg">
                Cast
              </Link>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function YourReading({ concepts }: { concepts: boolean }) {
  const stats = useReadingStats();
  return <ReadingFigures stats={stats.data} concepts={concepts} />;
}

/**
 * What your reading adds up to, on every device: pages, time, and ideas
 * held once there is a technical book. Until the numbers arrive the
 * figures hold their space. Presentational, so the gallery can render it.
 */
export function ReadingFigures({ stats, concepts }: { stats?: ReadingStats; concepts: boolean }) {
  const heading = useId();
  return (
    <section className="sh-sec sh-stats" aria-labelledby={heading} aria-busy={!stats}>
      <span className="sh-kicker" id={heading}>
        Your reading
      </span>
      <div className={cx('sh-figrow', !stats && 'hold')}>
        <Figure label="Pages read">{count(stats?.pages_read ?? 0)}</Figure>
        <Figure label="Time reading">
          {readingTime(stats?.seconds_read ?? 0).map(([n, unit], i) => (
            <Fragment key={unit}>
              {i ? ' ' : ''}
              {n}
              <small>{` ${unit}`}</small>
            </Fragment>
          ))}
        </Figure>
        {concepts ? <Figure label="Ideas held">{count(stats?.ideas_held ?? 0)}</Figure> : null}
      </div>
    </section>
  );
}

/**
 * How each track is going, one row each: its name, which opens it, the
 * facts of its line, and Review when it has ideas due. Only tracks with
 * books on them; not drawn when there are none. Presentational, so the
 * gallery can render it.
 */
export function YourTracks({ tracks }: { tracks: ReaderTrack[] }) {
  const heading = useId();
  const shelves = tracks.filter((t) => t.books.length > 0);
  if (!shelves.length) return null;
  return (
    <section className="sh-sec sh-also" aria-labelledby={heading}>
      <span className="sh-kicker" id={heading}>
        Your tracks
      </span>
      <ul className="sh-rows">
        {shelves.map((t) => {
          const due = t.plan.capture === 'ideas' && t.plan.recall !== 'off' ? t.figures.due : 0;
          return (
            <li key={t.id} className="sh-row">
              <div className="sh-row-copy">
                <Link to="/tracks/$trackId" params={{ trackId: t.id }} className="sh-row-t">
                  {t.name}
                </Link>
                <span className="sh-row-m">{trackFacts(t).join(' · ')}</span>
              </div>
              {due ? (
                <Link to="/review/session" search={{ track: t.id }} className="btn btn-quiet sh-row-due">
                  {`Review ${count(due)} due`}
                </Link>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Takes a reader to Premium's page in place of Home, and remembers that it
 * did: it is shown this way once, before their first book.
 */
function ToPremium({ reader }: { reader: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    markPremiumShown(reader);
    // In Home's place in the history: Back from Premium leaves, it doesn't bounce.
    void navigate({ to: '/premium', replace: true });
  }, [reader, navigate]);
  return null;
}

/** Said on arriving from a payment: how long the reader has Premium, and what it changed. */
function Paid() {
  const paid = useRouterState({ select: (r) => r.location.state.paid === true });
  const access = accessOf(useMe().data);
  return paid && access && hasPremium(access) ? <PaidView access={access} /> : null;
}

/** The note itself. Presentational, so the gallery can render it. */
export function PaidView({ access, now }: { access: Pick<Access, 'until'>; now?: number }) {
  const heading = useId();
  return (
    <section className="sh-sec" aria-labelledby={heading} role="status">
      <div className="card home-note">
        <b id={heading}>{`You have Premium until ${day(access.until, now)}`}</b>
        <p>Your payment went through. Add as many books as you want: none of them uses a credit.</p>
      </div>
    </section>
  );
}

/** In the last days of the free days or of a paid month: when it ends, and where to pay. */
function RunningOut() {
  const access = accessOf(useMe().data);
  return access && runningOut(access) ? <RunningOutView access={access} /> : null;
}

/** The note itself. Presentational, so the gallery can render it. */
export function RunningOutView({ access, now }: { access: Pick<Access, 'state' | 'until'>; now?: number }) {
  const heading = useId();
  const paid = access.state === 'paid';
  return (
    <section className="sh-sec" aria-labelledby={heading}>
      <div className="card home-note">
        <b id={heading}>
          {paid
            ? `Your month ends on ${day(access.until, now)}`
            : `Your free days end on ${day(access.until, now)}`}
        </b>
        <p>
          {paid
            ? 'Another month starts when this one ends. Nothing renews by itself.'
            : 'A month you pay for now starts when they end.'}
        </p>
        <div className="row-flex">
          <Link to="/premium" className="sh-more">
            Pay for a month
          </Link>
        </div>
      </div>
    </section>
  );
}

/** This device's memory that the note about tracks has been read. */
const NOTE_READ = 'wellread.tracks-note';

/**
 * Said once to a reader whose books were put on the ready-made tracks for
 * them: that tracks exist, and what the two do. "Got it" puts it away for
 * good on this device.
 */
function TracksNote() {
  const [read, setRead] = useState(() => {
    try {
      return localStorage.getItem(NOTE_READ) === '1';
    } catch {
      return false;
    }
  });
  if (read) return null;
  return (
    <TracksNoteView
      onRead={() => {
        try {
          localStorage.setItem(NOTE_READ, '1');
        } catch {
          // Without storage it is read for this visit.
        }
        setRead(true);
      }}
    />
  );
}

/** The note itself. Presentational, so the gallery can render it. */
export function TracksNoteView({ onRead }: { onRead: () => void }) {
  const heading = useId();
  return (
    <section className="sh-sec" aria-labelledby={heading}>
      <div className="card home-note">
        <b id={heading}>Your books sit on tracks</b>
        <p>
          Study asks you about a book’s ideas. Stories keeps a novel’s cast and story. Make a track of your
          own for anything else.
        </p>
        <div className="row-flex">
          <Link to="/tracks/about" className="sh-more">
            How tracks work
          </Link>
          <Link to="/tracks/new" className="sh-more">
            New track
          </Link>
          <span className="sp" />
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRead}>
            Got it
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * The other books you've started, one row each: the cover, where you are and
 * how far in. The row opens the book; Resume, shown when the row is pointed
 * at, goes back to your place. Not drawn when there are none. Presentational,
 * so the gallery can render it.
 */
export function AlsoReading({ books }: { books: Book[] }) {
  const heading = useId();
  if (!books.length) return null;
  return (
    <section className="sh-sec sh-also" aria-labelledby={heading}>
      <span className="sh-kicker" id={heading}>
        Also reading
      </span>
      <ul className="sh-rows">
        {books.map((b) => {
          const read = b.read_percent;
          return (
            <li key={b.id} className="sh-row">
              <Cover title={b.title} author={b.author} src={b.cover_url} ground={b.ground} size="sm" />
              <div className="sh-row-copy">
                <Link to="/books/$bookId" params={{ bookId: b.id }} className="sh-row-t">
                  {b.title}
                </Link>
                <span className="sh-row-m">{shelfLine(b)}</span>
              </div>
              <Meter value={read} label="Read" />
              <span className="sh-row-n">{`${read}%`}</span>
              <Link {...continueTarget(b)} className="btn btn-quiet" aria-label={`Resume ${b.title}`}>
                Resume
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="fig">
      <b>{children}</b>
      <span>{label}</span>
    </div>
  );
}
